/**
 * router/index.ts
 *
 * Automatic routes for `./src/pages/*.vue`
 */

import { createRouter, createWebHistory } from 'vue-router'
import { routes, handleHotUpdate } from 'vue-router/auto-routes'
// Composables
import { getCurrentUser } from 'vuefire'
import { useDatabaseStore } from '@/stores/database'
import { useRegistryStore } from '@/stores/registry'
import { loginMark, loginTime } from '@/utils/logger'

async function hasCompletedOnboarding () {
    try {
        return await useRegistryStore().getHasCompletedOnboarding()
    } catch {
        return true
    }
}

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    // Vue Router awaits nextTick() itself before calling this (and sets
    // history.scrollRestoration = 'manual' for us) - so the target route's DOM is
    // already patched by the time we get here; no extra delay needed here.
    scrollBehavior (to, from, savedPosition) {
        return savedPosition || { top: 0 }
    },
})

// todo https://kingstoncodes.hashnode.dev/firebase-authentication-and-vue-router

router.beforeEach(async to => {  // (to, from)
    // loginMark(`router guard start -> ${String(to.name)}`)
    // const user = await loginTime('router guard getCurrentUser', () => getCurrentUser())
    const user = await getCurrentUser()

    // Rescue tools must stay reachable even when signed out or the database is broken
    if (to.name === '/developer') {
        return true
    }

    // The login page is the main page; a signed-in user goes straight to the app
    if (to.name === '/') {
        if (!user) {
            return true
        }

        try {
            // await loginTime('router guard whenReady (from login page)', () => useDatabaseStore().whenReady())
            await useDatabaseStore().whenReady()
        } catch {
            return true
        }

        if (!await hasCompletedOnboarding()) {
            return { path: '/onboarding' }
        }

        const redirect = to.query.redirect
        return typeof redirect === 'string' && redirect.startsWith('/') ? redirect : { path: '/folders' }
    }

    if (user) {
        // loginMark('beforeEach: before database.wait')
        // Pages read the per-user database on mount, so wait for App.vue to initialize it
        try {
            // await loginTime(`router guard whenReady -> ${String(to.name)}`, () => useDatabaseStore().whenReady())
            await useDatabaseStore().whenReady()
        } catch {
            return { path: '/' }
        }

        // loginMark('beforeEach: AFTER database.wait')

        // A user who hasn't completed the onboarding yet lands on it instead of the folder list
        if (to.name === '/folders/' && !await hasCompletedOnboarding()) {
            return { path: '/onboarding' }
        }

        return true
    }

    return {
        path: '/',
        query: {
            // we keep the current path in the query so we can
            // redirect to it after login with
            // `router.push(route.query.redirect || '/')`
            redirect: to.fullPath,
        },
    }
})

if (import.meta.hot) {
    handleHotUpdate(router)
}

// router.isReady().then(() => {
//     localStorage.removeItem('vuetify:dynamic-reload')
// })

export default router
